/* eslint-disable @angular-eslint/prefer-standalone */
import { Component, OnDestroy, OnInit, signal, effect } from '@angular/core';
import { Router } from '@angular/router';
import { ConfirmationService } from 'primeng/api';
import {
  EMPTY,
  from,
  Observable,
  of,
  Subscription,
  combineLatest,
  timer
} from 'rxjs';
import {
  catchError,
  filter,
  first,
  map,
  mergeMap,
  shareReplay,
  skip,
  switchMap,
  tap
} from 'rxjs/operators';
import { CapabilityActions } from 'src/app/store/capability/capability.action';
import { CapabilityQuery } from 'src/app/store/capability/capability.query';
import { StateActions } from 'src/app/store/state/state.action';
import { StateQuery } from 'src/app/store/state/state.query';
import { UserAccountAction } from 'src/app/store/userAccount/user-account.action';
import { UserAccountQuery } from 'src/app/store/userAccount/user-account.query';
import { VendorActions } from 'src/app/store/vendor/vendor.action';
import {
  IVendorLocation,
  IVendorOnboardingResult,
  IVendorOnboardingSave,
  mockVendorLocation
} from 'src/app/store/vendor/vendor.model';
import { VendorQuery } from 'src/app/store/vendor/vendor.query';
import {
  OktaService,
  LoggerDatadogService,
  InteractionsApi,
  IdentityApi,
  MapboxService,
  parseAddress,
  VendorAPI,
  injectFlag,
  UserAccount,
  VendorModelResponse,
  UserSession
} from 'sustainment-component';
import {
  ICapabilitytProcess,
  ICapability,
  SupplierListModel,
  VendorCreateRequest,
  IVendorProcess,
  IVendorIndustry
} from 'sustainment-component';
import { OnboardingAPI } from 'src/app/api/onboarding.api';
import { AuthService } from 'src/app/services/auth.service';
import { EMPTY_GUID } from 'src/app/utils/contants';
import { CUSTOM_LANDING_PAGE_REDIRECT_QUERY_PARAM_KEY } from 'src/app/constants/url-constants';
@Component({
  selector: 'app-registration-container',
  styleUrls: ['./registration.container.scss'],
  templateUrl: './registration.container.html',
  providers: [ConfirmationService],
  standalone: false
})
export class RegistrationContainerComponent implements OnInit, OnDestroy {
  public vendorInfo: Partial<IVendorLocation>;

  public processes: ICapabilitytProcess[] = [];
  public industries: ICapabilitytProcess[] = [];
  public preloadVendor$: Observable<SupplierListModel | null>;
  public preloadVendor: SupplierListModel | null;
  public loaded = false;
  public invitationLoading = false;
  public isSaved = false;
  public isNotBuyer = false;
  public sustainmentid: string;

  public originalProcesses: ICapabilitytProcess[] = [];

  private _subscriptions = new Subscription();
  private _localStorageVariable = 'vendor-info';
  private _refCode$: Observable<string | null>;
  public refCode: string | null;

  public subprocessSet: Set<string> = new Set<string>();
  public userRoles$: Observable<import('sustainment-component').UserRole[]>;

  public registrationComplete = signal<boolean>(false);
  public navigationTarget = signal<string | null>(null);
  public storeUpdated = signal<boolean>(false);
  public shouldShowWelcomeScreen = signal<boolean>(false);

  public isManufacturerResearchAgentOn = injectFlag(
    'pillars.agents.research.enabled'
  );
  public processingProfile = signal<boolean>(false);

  public constructor(
    private capabilityActions: CapabilityActions,
    private userAccountAction: UserAccountAction,
    private userAccountQuery: UserAccountQuery,
    private okta: OktaService,
    private vendorActions: VendorActions,
    private stateQuery: StateQuery,
    private stateAction: StateActions,
    private vendorQuery: VendorQuery,
    private vendorApi: VendorAPI,
    private router: Router,
    private capabilityQuery: CapabilityQuery,
    private interactionsApi: InteractionsApi,
    private identityApi: IdentityApi,
    public loggerDatadogService: LoggerDatadogService,
    private confirmationService: ConfirmationService,
    private _onboardingApi: OnboardingAPI,
    private _authService: AuthService,
    private _mapSvc: MapboxService
  ) {
    // AVJ: we need to investigate whether this is necessary or not
    effect(() => {
      this.registrationComplete();
      this.navigationTarget();
      this.storeUpdated();
    });
  }

  public ngOnInit(): void {
    const urlParams = new URLSearchParams(window.location.search);
    const urlRefCode = urlParams.get('ref');

    if (urlRefCode) {
      this.loaded = false;
      this.invitationLoading = true;
    }

    this.userRoles$ = this.identityApi.getUserRoles().pipe(
      filter((roles) => !!roles && roles.length > 0),
      shareReplay(1)
    );

    this.stateAction.getStateData();
    this.loadIndustryAndProcesses();

    if (!urlRefCode) {
      this._refCode$ = from(this.okta.widget.authClient.getUser()).pipe(
        map((user) => user.ref?.toString() || null),
        shareReplay(1)
      );

      this._subscriptions.add(
        this._refCode$.subscribe((code) => {
          this.refCode = code;
          if (code) {
            this.loaded = false;
            this.invitationLoading = true;
          }
        })
      );
    }

    if (urlRefCode) {
      this.refCode = urlRefCode;
      this._refCode$ = of(urlRefCode);

      const invite$ = this.interactionsApi.getOdysseus(urlRefCode);

      this.preloadVendor$ = invite$.pipe(
        catchError(() => of(null)),
        tap(
          (
            odysseus: { stillValid?: boolean; sustainmentId: string } | null
          ) => {
            if (odysseus && !odysseus.stillValid) {
              this.confirmationService.confirm({
                key: 'cancelOnboardingPreselection',
                header: 'Register your own business',
                message:
                  'This invite seems to have already been used. ' +
                  'You can register your business to connect and work with others. ' +
                  'If you do not wish to proceed, please close the following window.',
                acceptLabel: 'Create my profile',
                acceptIcon: 'null',
                rejectVisible: false,
                accept: () => {
                  this.preloadVendor$ = of(null);
                }
              });
            }
          }
        ),
        mergeMap(
          (result: { stillValid?: boolean; sustainmentId: string } | null) =>
            result?.stillValid
              ? this._onboardingApi.getOpensearchVendor(result.sustainmentId)
              : of(null)
        ),
        catchError(() => of(null)),
        tap((preload) => {
          this.preloadVendor = preload;
          if (urlRefCode && preload) {
            this.vendorInfo = this.mapSupplierListToVendorLocation(preload);

            this._mapSvc
              .getGeocodingWithType(
                this.vendorInfo.primaryAddress as string,
                'address',
                true
              )
              .subscribe((r) => {
                const mapSearchRes = r?.features[0];
                const parsedAddress = mapSearchRes?.place_name
                  ? parseAddress(mapSearchRes)
                  : null;

                if (parsedAddress && this.stateQuery.getValue().states) {
                  this.vendorInfo.address = {
                    ...parsedAddress,
                    stateId: Number(
                      this.stateQuery.getByNameOrAbbreviation(
                        parsedAddress.stateAbbreviation || 'OU'
                      )?.stateId
                    )
                  };
                }

                this.loaded = true;
                this.invitationLoading = false;
              });
          } else {
            this.loaded = true;
            this.invitationLoading = false;
          }
        })
      );

      this._subscriptions.add(this.preloadVendor$.subscribe());
    } else {
      const invite$ = this._refCode$.pipe(
        mergeMap((code) =>
          code ? this.interactionsApi.getOdysseus(code.toString()) : of(null)
        )
      );

      this.preloadVendor$ = invite$.pipe(
        catchError(() => of(null)),
        tap(
          (
            odysseus: { stillValid?: boolean; sustainmentId: string } | null
          ) => {
            if (odysseus && !odysseus.stillValid)
              this.confirmationService.confirm({
                key: 'cancelOnboardingPreselection',
                header: 'Register your own business',
                message:
                  'This invite seems to have already been used. ' +
                  'You can register your business to connect and work with others. ' +
                  'If you do not wish to proceed, please close the following window.',
                acceptLabel: 'Create my profile',
                acceptIcon: 'null',
                rejectVisible: false,
                accept: () => {
                  this.preloadVendor$ = of(null);
                }
              });
          }
        ),
        mergeMap(
          (result: { stillValid?: boolean; sustainmentId: string } | null) =>
            result?.stillValid
              ? this._onboardingApi.getOpensearchVendor(result.sustainmentId)
              : of(null)
        ),
        catchError(() => of(null)),
        tap((preload) => {
          this.preloadVendor = preload;
          if (this.refCode && preload) {
            this.vendorInfo = this.mapSupplierListToVendorLocation(preload);

            this._mapSvc
              .getGeocodingWithType(
                this.vendorInfo.primaryAddress as string,
                'address',
                true
              )
              .subscribe((r) => {
                const mapSearchRes = r?.features[0];
                const parsedAddress = mapSearchRes?.place_name
                  ? parseAddress(mapSearchRes)
                  : null;

                if (parsedAddress && this.stateQuery.getValue().states) {
                  this.vendorInfo.address = {
                    ...parsedAddress,
                    stateId: Number(
                      this.stateQuery.getByNameOrAbbreviation(
                        parsedAddress.stateAbbreviation || 'OU'
                      )?.stateId
                    )
                  };
                }

                this.loaded = true;
              });
          } else {
            this.loaded = true;
          }
        })
      );

      this._subscriptions.add(
        this._refCode$
          .pipe(
            mergeMap(() => this.preloadVendor$),
            tap((preload) => {
              this.preloadVendor = preload;
              if (this.refCode && preload) {
                this.vendorInfo = this.mapSupplierListToVendorLocation(preload);

                this._mapSvc
                  .getGeocodingWithType(
                    this.vendorInfo.primaryAddress as string,
                    'address',
                    true
                  )
                  .subscribe((r) => {
                    const mapSearchRes = r?.features[0];
                    const parsedAddress = mapSearchRes?.place_name
                      ? parseAddress(mapSearchRes)
                      : null;

                    if (parsedAddress && this.stateQuery.getValue().states) {
                      this.vendorInfo.address = {
                        ...parsedAddress,
                        stateId: Number(
                          this.stateQuery.getByNameOrAbbreviation(
                            parsedAddress.stateAbbreviation || 'OU'
                          )?.stateId
                        )
                      };
                    }

                    this.loaded = true;
                    this.invitationLoading = false;
                  });
              } else {
                this.loaded = true;
                this.invitationLoading = false;
              }
            })
          )
          .subscribe()
      );
    }

    const existingSustainmentId = this.userAccountQuery.sustainmentId;
    if (!existingSustainmentId || existingSustainmentId === EMPTY_GUID) {
      if (!urlRefCode) {
        this._subscriptions.add(
          combineLatest([
            this.userRoles$.pipe(first()),
            this._refCode$.pipe(first())
          ]).subscribe(([_, refCode]) => {
            if (!refCode) {
              this.loaded = true;
            }
          })
        );
      }
      return;
    }

    this.vendorActions.getVendorData();

    if (!urlRefCode) {
      this._subscriptions.add(
        combineLatest([
          this.vendorQuery.select((v) => v.vendor).pipe(first()),
          this.userRoles$.pipe(first()),
          this._refCode$.pipe(first())
        ]).subscribe(([vendor, _, refCode]) => {
          if (vendor?.name && this.router.url.includes('registration')) {
            this.isSaved = true;
            this.router.navigate(['/dashboard']);
          } else {
            if (!refCode) {
              this.loaded = true;
            }
          }
        })
      );
    } else {
      this._subscriptions.add(
        combineLatest([
          this.vendorQuery.select((v) => v.vendor).pipe(first()),
          this.userRoles$.pipe(first())
        ]).subscribe(([vendor]) => {
          if (vendor?.name && this.router.url.includes('registration')) {
            this.isSaved = true;
            this.router.navigate(['/dashboard']);
          }
        })
      );
    }

    const invite$ = this._refCode$.pipe(
      mergeMap((code) =>
        code ? this.interactionsApi.getOdysseus(code.toString()) : of(null)
      )
    );

    this._subscriptions.add(
      this._refCode$.subscribe((code) => (this.refCode = code))
    );

    this.preloadVendor$ = invite$.pipe(
      catchError(() => of(null)),
      tap(
        (odysseus: { stillValid?: boolean; sustainmentId: string } | null) => {
          if (odysseus && !odysseus.stillValid)
            this.confirmationService.confirm({
              key: 'cancelOnboardingPreselection',
              header: 'Register your own business',
              message:
                'This invite seems to have already been used. ' +
                'You can register your business to connect and work with others. ' +
                'If you do not wish to proceed, please close the following window.',
              acceptLabel: 'Create my profile',
              acceptIcon: 'null',
              rejectVisible: false,
              accept: () => {
                this.preloadVendor$ = of(null);
              }
            });
        }
      ),
      mergeMap(
        (result: { stillValid?: boolean; sustainmentId: string } | null) =>
          result?.stillValid
            ? this._onboardingApi.getOpensearchVendor(result.sustainmentId)
            : of(null)
      ),
      catchError(() => of(null))
    );

    this._subscriptions.add(
      this._refCode$
        .pipe(
          mergeMap(() => this.preloadVendor$),
          tap((preload) => {
            this.preloadVendor = preload;
            if (this.refCode && preload) {
              this.vendorInfo = this.mapSupplierListToVendorLocation(preload);

              this._mapSvc
                .getGeocodingWithType(
                  this.vendorInfo.primaryAddress as string,
                  'address',
                  true
                )
                .subscribe((r) => {
                  const mapSearchRes = r?.features[0];
                  const parsedAddress = mapSearchRes?.place_name
                    ? parseAddress(mapSearchRes)
                    : null;

                  if (parsedAddress && this.stateQuery.getValue().states) {
                    this.vendorInfo.address = {
                      ...parsedAddress,
                      stateId: Number(
                        this.stateQuery.getByNameOrAbbreviation(
                          parsedAddress.stateAbbreviation || 'OU'
                        )?.stateId
                      )
                    };
                  }

                  this.loaded = true;
                });
            } else {
              this.loaded = true;
            }
          })
        )
        .subscribe()
    );

    const storageSaved = localStorage.getItem(this._localStorageVariable);
    const sustainmentId = this.userAccountQuery.sustainmentId;
    if (storageSaved) {
      this.vendorInfo = JSON.parse(storageSaved);
      this.loggerDatadogService.info('onboarding resumed', {
        sustainmentId,
        data: this.vendorInfo,
        action: 'onboarding'
      });
    } else {
      this.loggerDatadogService.info('started onboarding from scratch', {
        sustainmentId,
        action: 'onboarding'
      });
      this.vendorInfo = { ...mockVendorLocation };
    }
  }

  public logout(): void {
    this._authService.logout();
  }

  public ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  public onFinishRegistration(
    data: IVendorOnboardingResult,
    roles?: { id: number; text: string }[],
    problems?: { id: number; text: string }[]
  ): void {
    if (this.preloadVendor) {
      data.sustainmentId = this.preloadVendor.sustainmentId;
    }

    this.preloadVendor$ = of(null);
    this.loaded = false;

    this.registrationComplete.set(false);
    this.navigationTarget.set(null);
    this.storeUpdated.set(false);

    const createVendor = this.prepareVendorData(data);

    this.executeRegistrationFlow(createVendor, roles, problems);
  }

  /**
   * Prepare vendor data for the registration API calls
   */
  private prepareVendorData(
    data: IVendorOnboardingResult
  ): VendorCreateRequest {
    const industries = data.industries.map((i) => ({
      id: i.industry.id,
      orderIndex: 0,
      isFavorite: i.isFavorite || false
    }));

    let processes = data.processes;

    if (processes?.length > 0) {
      processes = processes.map((p) => {
        const process = p as { code: string; name: string; isNew: boolean };

        return {
          processCode: process.code,
          name: process.name,
          isCustom: process.isNew
        };
      });
    }

    const createVendor: VendorCreateRequest = {
      ...data,
      primaryAddress: {
        ...data.primaryAddress,
        vendorAddressId: 0,
        unit: '',
        stateId: 0
      },
      industries,
      processes: processes as { id: number; orderIndex: number }[]
    };

    const vendorState = this.stateQuery.getByNameOrAbbreviation(
      data.primaryAddress.state || 'OU'
    );

    if (vendorState) createVendor.primaryAddress.stateId = vendorState?.stateId;

    return createVendor;
  }

  /**
   * Execute the registration flow with prepared vendor data
   */
  private executeRegistrationFlow(
    createVendor: VendorCreateRequest,
    roles?: { id: number; text: string }[],
    problems?: { id: number; text: string }[]
  ): void {
    this.identityApi
      .registerOrganization(createVendor.name, createVendor.description)
      .pipe(
        mergeMap(() => this.getUserAccount()),
        tap((userAccount) => {
          this.userAccountAction.setUserAccount(userAccount);
        }),
        filter(
          (userAccount) =>
            !!userAccount.organizations && userAccount.organizations.length > 0
        ),
        switchMap((userAccount) =>
          this.createVendor(userAccount, createVendor).pipe(
            tap((vendorResponse) => {
              createVendor.sustainmentId = vendorResponse?.sustainmentId;

              const hasAnOrg = userAccount.organizations.length > 0;
              const orgSustainmentIdMatchesCreationResponseSustainmentId =
                hasAnOrg &&
                userAccount.organizations[0].sustainmentId ===
                  vendorResponse?.sustainmentId;

              // AVJ: This fix is being done for the edge case that a user
              // claims an existing company and the userAccount object
              // is still holding the old sustainmentId and thus causing
              // a sustainmentId mismatch after claiming. The useraccount
              // could also be refetched but this is saving an additional
              // call.
              if (hasAnOrg && !orgSustainmentIdMatchesCreationResponseSustainmentId) {
                this.userAccountAction.updateOrgSustainmentId(
                  vendorResponse?.sustainmentId
                );
              }
            })
          )
        ),
        tap(() => {
          this.userAccountAction.showedWelcome();
        }),
        mergeMap(() => this.processInvitationsAndLogin(createVendor))
      )
      .subscribe({
        next: (userSession) => {
          this.finalizeRegistration(createVendor, userSession, roles, problems);

          const customLandingRoute = sessionStorage.getItem(
            CUSTOM_LANDING_PAGE_REDIRECT_QUERY_PARAM_KEY
          );

          if (customLandingRoute) {
            this.storeUpdated.set(true);
            this.registrationComplete.set(true);

            return this.navigateToCustomLandingRoute(customLandingRoute);
          }

          const isNotBuyer = createVendor.isSupplier || createVendor.isOther;
          this.shouldShowWelcomeScreen.set(isNotBuyer);

          if (!isNotBuyer) {
            this.setupStoreUpdateDetection('/home');
          } else {
            this.storeUpdated.set(true);
            this.registrationComplete.set(true);
          }
        },
        error: (err) => {
          this.loggerDatadogService.error('Registration error:', err);
          this.loaded = true;
        }
      });
  }

  private getUserAccount(): Observable<UserAccount> {
    return this.identityApi.getUserAccount({
      username: this.userAccountQuery.getValue().userAccount.userName,
      firstName: this.userAccountQuery.getValue().userAccount.firstName,
      lastName: this.userAccountQuery.getValue().userAccount.lastName
    });
  }

  private createVendor(
    userAccount: UserAccount,
    createVendor: VendorCreateRequest
  ): Observable<VendorModelResponse> {
    const organizationSustainmentId =
      userAccount.organizations[0].sustainmentId;

    createVendor.sustainmentId = organizationSustainmentId;

    return this.vendorApi.createVendorV3({
      ...createVendor,
      pubNubUserId: this.isManufacturerResearchAgentOn()
        ? this.userAccountQuery.getUserId()
        : undefined
    });
  }

  private processInvitationsAndLogin(
    createVendor: VendorCreateRequest
  ): Observable<UserSession> {
    if (this._refCode$) {
      this._refCode$
        .pipe(
          first(),
          mergeMap((code) => {
            if (code) {
              return this.interactionsApi.acceptInvitation(
                code,
                createVendor.sustainmentId
              );
            }
            if (createVendor.sustainmentId) {
              return this.interactionsApi.checkOtherInvitations(
                createVendor.sustainmentId
              );
            }
            return EMPTY;
          })
        )
        .subscribe({
          next: () => {
            this.loggerDatadogService.info('invitation accepted', {
              data: createVendor,
              action: 'onboarding'
            });
          },
          error: (error) => {
            this.loggerDatadogService.error('failed to accept invitation', {
              data: createVendor,
              action: 'onboarding',
              error
            });
          }
        });
    }

    return this.identityApi.login(createVendor.sustainmentId);
  }

  private finalizeRegistration(
    createVendor: VendorCreateRequest,
    userSession: UserSession,
    roles?: { id: number; text: string }[],
    problems?: { id: number; text: string }[]
  ): void {
    if (userSession) {
      this.userAccountAction.setUserSession(userSession);
    }

    if (roles?.length) {
      this.identityApi
        .updateUserRoles(
          this.userAccountQuery.sustainmentId,
          roles.map((e) => e.id)
        )
        .subscribe();
      this.userAccountAction.updateUserRoles(roles.map((e) => e.text));
    }

    if (problems?.length) {
      this.userAccountAction.updateUserProblems(problems);
    }

    this.loaded = true;
    this.isSaved = true;
    this.isNotBuyer = createVendor.isSupplier || createVendor.isOther;
    this.processingProfile.set(
      this.isManufacturerResearchAgentOn() &&
        createVendor.isSupplier &&
        !!createVendor.website
    );
    this.sustainmentid = this.userAccountQuery.sustainmentId;

    localStorage.removeItem(this._localStorageVariable);

    const userEmail = this.userAccountQuery.email || '';
    const emailPart = userEmail ? `-${userEmail}` : '';
    localStorage.removeItem(`registrationFormData${emailPart}`);

    const vendor = this.vendorQuery.getValue()?.vendor;

    if (vendor) vendor.name = createVendor.name;
  }

  private setupStoreUpdateDetection(targetRoute: string): void {
    this.navigationTarget.set(targetRoute);

    this._subscriptions.add(
      this.vendorQuery
        .select()
        .pipe(skip(1), first())
        .subscribe(() => {
          this.storeUpdated.set(true);
          this.registrationComplete.set(true);
        })
    );

    this._subscriptions.add(
      timer(1000).subscribe(() => {
        if (!this.storeUpdated()) {
          this.storeUpdated.set(true);
          this.registrationComplete.set(true);
        }
      })
    );
  }

  public loadIndustryAndProcesses(): void {
    if (!this.vendorInfo || !this._authService.isAuthenticated$) {
      return;
    }

    this.capabilityActions.getCapabilityData();
    this._subscriptions.add(
      this.capabilityQuery.select().subscribe((capabilities) => {
        this.originalProcesses = capabilities.processList;
        this.processes =
          capabilities.processList?.map((process) => {
            const using = this.vendorInfo.processes?.find(
              (vendorProcess) => vendorProcess.process.code === process.code
            );

            const subprocessListTemp: ICapability[] = [];
            process.subprocess?.forEach((subprocess: ICapability) => {
              const tempSubprocess: ICapability = {
                name: subprocess.name,
                code: subprocess.code,
                description: subprocess.description,
                using: this.subprocessSet.has(subprocess.code),
                id: subprocess.id
              };
              subprocessListTemp.push(tempSubprocess);
            });

            return {
              name: process.name,
              code: process.code,
              description: process.description,
              using: !!using,
              favorite: !!using && using.isFavorite,
              id: process.id,
              subprocess: subprocessListTemp
            };
          }) || [];

        this.industries =
          capabilities.industryList?.map((industry) => {
            const using = this.vendorInfo.industries?.find(
              (vendorIndustry) => vendorIndustry.industry.code === industry.code
            );
            return {
              name: industry.name,
              code: industry.code,
              description: industry.description,
              using: !!using,
              favorite: !!using && using.isFavorite,
              id: industry.id
            };
          }) || [];
      })
    );
  }

  public updateVendorInfo(data: IVendorOnboardingSave): void {
    this.userAccountAction.updateOrganizationName(data.name);
    const vendorInfoJson = this.getJsonVendor(data);
    localStorage.setItem(this._localStorageVariable, vendorInfoJson);
  }

  public cancelPreselection(): void {
    this.confirmationService.confirm({
      key: 'cancelOnboardingPreselection',
      header: 'Register your own business',
      message:
        'You can register your own business to connect and work with others. ' +
        'If you do not wish to proceed, please close the following window.',
      acceptLabel: 'Create my profile',
      acceptIcon: 'null',
      rejectVisible: false,
      accept: () => {
        this.preloadVendor$ = of(null);
      }
    });
  }

  public requestToJoinCompany(company: SupplierListModel): void {
    if (!company.isRegistered) return;
    this.identityApi
      .requestToJoinCompany(company.sustainmentId)
      .subscribe(() => {
        localStorage.clear();
        this.router.navigateByUrl('/pending-activation', {
          state: { companyName: company.name }
        });
      });
  }

  private getJsonVendor(data: IVendorOnboardingSave): string {
    const formSaved = {
      name: data.name,
      address: {
        ...data.address,
        stateId:
          data.address?.stateId ||
          this.stateQuery.getByNameOrAbbreviation(data.address?.state || '')
            ?.stateId
      },
      processes: [],
      industries: [],
      website: data.website
    };

    return JSON.stringify(formSaved);
  }

  private mapSupplierListToVendorLocation(
    supplier: SupplierListModel
  ): Partial<IVendorLocation> {
    return {
      sustainmentId: supplier.sustainmentId,
      name: supplier.name,
      descriptionPlaceholder: supplier.description,
      primaryAddress:
        supplier.address.addressLine +
        ', ' +
        supplier.address.city +
        ', ' +
        supplier.address.state +
        ' ' +
        supplier.address.zip,

      website: supplier.website,
      processes: supplier.processes as unknown as IVendorProcess[],
      industries: supplier.industries as unknown as IVendorIndustry[]
    };
  }

  /**
   * Utilizes the custom landing page query param (when present - set from external sites that link to this portal)
   * to navigate to a particular route in the app after registration is finished.
   *
   * The route stored in the query param must be exactly the same as the route in the app.
   */
  private navigateToCustomLandingRoute(route: string): void {
    if (!route) return;

    this.router.navigateByUrl(route);

    sessionStorage.removeItem(CUSTOM_LANDING_PAGE_REDIRECT_QUERY_PARAM_KEY);
  }
}
